import React, { forwardRef, useEffect, useImperativeHandle, useLayoutEffect, useRef, useState } from 'react';

// Decode the next asset in a second element. Keep the previous frame visible until
// the replacement has submitted a frame; never erase the screen on a source swap.
const SeamlessVideoPlayer = forwardRef(function SeamlessVideoPlayer({
    url, nextUrl = '', autoPlay = true, controls = false, className = '',
    onEnded, onPlay, onPlaying, onPause, onReady, onError, onActiveVideo,
}, ref) {
    const videos = useRef([]);
    const active = useRef(0);
    const callbacks = useRef({});
    const generation = useRef(0);
    const heldFrame = useRef(null);
    const [visible, setVisible] = useState(0);
    const [holding, setHolding] = useState(false);
    callbacks.current = { onEnded, onPlay, onPlaying, onPause, onReady, onError, onActiveVideo };

    useImperativeHandle(ref, () => ({
        getInternalPlayer: () => videos.current[active.current],
        getCurrentTime: () => videos.current[active.current]?.currentTime || 0,
        getDuration: () => videos.current[active.current]?.duration || 0,
        seekTo: value => { if (videos.current[active.current]) videos.current[active.current].currentTime = value; },
    }), []);

    useLayoutEffect(() => {
        if (!url) return undefined;
        const ticket = ++generation.current;
        const previous = videos.current[active.current];
        let index = videos.current.findIndex(video => video?.dataset.playbackUrl === url);
        if (index === -1) index = previous?.dataset.playbackUrl ? 1 - active.current : active.current;
        const video = videos.current[index];
        active.current = index;
        let frameRequest;
        let fallback;
        let revealed = false;
        let playbackRequested = false;
        const current = () => generation.current === ticket;
        const reveal = () => {
            if (!current() || revealed || video.readyState < 2) return;
            revealed = true;
            if (previous !== video) previous?.pause();
            setVisible(index);
            setHolding(false);
        };
        const ready = () => {
            if (!current() || video.readyState < 2) return;
            callbacks.current.onReady?.();
            if (video.requestVideoFrameCallback) frameRequest = video.requestVideoFrameCallback(reveal);
            // A paused first frame or older browser may not produce a frame callback.
            clearTimeout(fallback);
            fallback = setTimeout(reveal, 120);
            if (autoPlay && !playbackRequested) {
                playbackRequested = true;
                video.play().catch(error => {
                if (current() && error.name !== 'AbortError') callbacks.current.onError?.(error);
                });
            }
        };
        video.addEventListener('canplay', ready);
        if (video.dataset.playbackUrl !== url || video.error) {
            video.dataset.playbackUrl = url;
            delete video.dataset.scenario;
            video.src = url;
            video.load();
        }
        video.playbackRate = previous?.playbackRate || 1;
        if (previous !== video && video.readyState >= 1) video.currentTime = 0;
        callbacks.current.onActiveVideo?.(video);
        if (video.readyState >= 2) ready();
        return () => {
            video.removeEventListener('canplay', ready);
            clearTimeout(fallback);
            if (frameRequest !== undefined) video.cancelVideoFrameCallback?.(frameRequest);
        };
    }, [url, autoPlay]);

    useEffect(() => {
        if (!url || !nextUrl || nextUrl === url || visible !== active.current) return;
        const spare = videos.current[1 - active.current];
        spare.pause();
        if (spare.dataset.playbackUrl !== nextUrl) {
            spare.dataset.playbackUrl = nextUrl;
            delete spare.dataset.scenario;
            spare.src = nextUrl;
            spare.load();
        } else if (spare.readyState >= 1 && spare.currentTime !== 0) spare.currentTime = 0;
    }, [url, nextUrl, visible]);

    useEffect(() => () => {
        generation.current += 1;
        videos.current.forEach(video => video?.pause());
    }, []);

    const event = (index, name, value) => {
        if (index === active.current) callbacks.current[name]?.(value);
    };
    const holdLastFrame = index => {
        if (index !== active.current) return;
        const video = videos.current[index];
        const canvas = heldFrame.current;
        if (video.readyState >= 2 && video.videoWidth && video.videoHeight && canvas) {
            try {
                canvas.width = video.videoWidth;
                canvas.height = video.videoHeight;
                const context = canvas.getContext('2d');
                if (context) {
                    context.drawImage(video, 0, 0);
                    setHolding(true);
                }
            } catch { /* The decoded video itself remains the fallback. */ }
        }
        callbacks.current.onEnded?.();
    };
    return (
        <div className={`seamlessVideoPlayer ${className}`} data-player-source={url || ''}>
            {[0, 1].map(index => (
                <video key={index} ref={video => { videos.current[index] = video; }}
                    className={`seamlessVideoSlot${index === visible ? ' isVisible' : ''}`}
                    controls={controls && index === visible} muted playsInline preload='auto'
                    aria-hidden={index !== visible} tabIndex={index === visible && controls ? 0 : -1}
                    disablePictureInPicture
                    onEnded={() => holdLastFrame(index)}
                    onPlay={value => event(index, 'onPlay', value)}
                    onPlaying={value => event(index, 'onPlaying', value)}
                    onPause={value => event(index, 'onPause', value)}
                    onLoadedMetadata={value => event(index, 'onReady', value)}
                    onSeeked={value => { if (index === active.current) setHolding(false); event(index, 'onReady', value); }}
                    onError={value => event(index, 'onError', value)}
                />
            ))}
            <canvas ref={heldFrame} className={`seamlessHeldFrame${holding ? ' isVisible' : ''}`} aria-hidden='true' />
        </div>
    );
});
export default SeamlessVideoPlayer;
